import { X, Link, Mail, MessageCircle, Send } from 'lucide-react'
import { useState } from 'react'
import Modal from './ui/Modal'
import Avatar from './ui/Avatar'

interface ShareModalProps {
  isOpen: boolean
  onClose: () => void
  post: {
    id: string
    author: {
      name: string
      username: string
      avatar?: string
    }
    content?: string
    mediaUrl?: string
  }
}

export default function ShareModal({ isOpen, onClose, post }: ShareModalProps) {
  const [copied, setCopied] = useState(false)
  const postUrl = `${window.location.origin}/post/${post.id}`

  const handleCopyLink = () => {
    navigator.clipboard.writeText(postUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleShareViaEmail = () => {
    window.location.href = `mailto:?subject=Check out this post&body=${encodeURIComponent(postUrl)}`
  }

  const handleShareViaSMS = () => {
    window.location.href = `sms:?body=${encodeURIComponent(postUrl)}`
  }

  const shareOptions = [
    {
      icon: Link,
      label: copied ? 'Link Copied!' : 'Copy Link',
      onClick: handleCopyLink,
      color: 'text-gray-700',
    },
    {
      icon: Mail,
      label: 'Share via Email',
      onClick: handleShareViaEmail,
      color: 'text-gray-700',
    },
    {
      icon: MessageCircle,
      label: 'Share via Messages',
      onClick: handleShareViaSMS,
      color: 'text-gray-700',
    },
    {
      icon: Send,
      label: 'Send via Direct Message',
      onClick: () => {
        // TODO: Implement DM functionality
        console.log('Share via DM')
      },
      color: 'text-gray-700',
    },
  ]

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold">Share Post</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 p-2 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Post Preview */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex gap-3">
            <Avatar src={post.author.avatar} alt={post.author.name} size="md" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">{post.author.name}</div>
              <div className="text-sm text-gray-500">@{post.author.username}</div>
              {post.content && (
                <p className="text-sm text-gray-900 mt-2 line-clamp-3">{post.content}</p>
              )}
            </div>
          </div>
        </div>

        {/* Share Options */}
        <div className="p-4">
          <div className="space-y-2">
            {shareOptions.map((option, index) => (
              <button
                key={index}
                onClick={option.onClick}
                className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className={`${option.color}`}>
                  <option.icon className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium">{option.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}
